import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import cx from 'classnames';
import { Button } from '@xingternal/button';
import { BodyCopy } from '@xingternal/typography';
import { redirectToRegister, redirectToLogin } from '@xingternal/pages-common';

import { ErrorContext } from '@xingternal/pages-page-error';
import EmployeesCardVertical from './EmployeesCardVertical';
import styles from './EmployeesCardVerticalContainer.css';

import { useNetworkRequestMutation } from './../../hooks/useNetworkRequestMutation';
import {
  addContactButtonDisabled,
  FOLLOW_EMPLOYEE_ID_PARAM,
  FOLLOW_EMPLOYEE_ACTION,
} from '../../utils';
import { NetworkRequestResponseUpdateCache } from '../../types';

type EmployeesCardVerticalContainerProps = {
  id: string;
  profileImage: {
    src: string;
    type: string;
    profileName: string;
    alt: string;
    connectionDegree?: number;
  };
  pageName?: string;
  profileUrl: string;
  profileName: string;
  position?: string;
  authenticated?: boolean;
  isCurrentUser?: boolean;
  relationship: string;
  flag?: string;
  hasPhoto?: boolean;
  updateEmployeesCache: NetworkRequestResponseUpdateCache;
};

type EmployeeCardFencingProps = {
  employeesNumber?: number;
};

const EmployeeCardFencing = ({
  employeesNumber = 100,
}: EmployeeCardFencingProps) => (
  <div className={cx(styles.card, styles.blurBackground)}>
    <BodyCopy
      fontWeight="bold"
      lineHeight="dense"
      size={'medium'}
      className={styles.registerText}
    >
      <FormattedMessage
        values={{ employeesNumber }}
        id="EP_EMPLOYEES_TEXT_CTA_REGISTER_TO_SEE"
      />
    </BodyCopy>
    <Button
      variant={'primary'}
      size={'small'}
      data-testid="register-button"
      onClick={() => redirectToRegister()}
    >
      <FormattedMessage id="EP_EMPLOYEES_BUTTON_REGISTER_NOW" />
    </Button>
  </div>
);

const EmployeesCardVerticalContainer = ({
  authenticated = false,
  isCurrentUser,
  relationship,
  id,
  pageName,
  profileUrl,
  flag,
  hasPhoto,
  updateEmployeesCache,
  ...rest
}: EmployeesCardVerticalContainerProps) => {
  const { showError } = useContext(ErrorContext);

  const [executeNetworkRequest, { loading: mutationLoading }] =
    useNetworkRequestMutation({
      showError,
      update: (cache, data) => updateEmployeesCache(cache, data, id, hasPhoto),
    });

  const addContact = () => {
    if (!authenticated) {
      redirectToLogin(`${FOLLOW_EMPLOYEE_ACTION}`, {
        [FOLLOW_EMPLOYEE_ID_PARAM]: id,
      });
    }
    return !mutationLoading && executeNetworkRequest(id);
  };

  const sendMessage = () => {
    window.location.assign(
      `/chats/new/${pageName}?contextId=entity_pages&entryPoint=entity_pages_main_employees_message`
    );
  };

  return (
    <EmployeesCardVertical
      {...rest}
      profileUrl={profileUrl}
      authenticated={authenticated}
      isCurrentUser={isCurrentUser}
      relationship={relationship}
      addContact={addContact}
      sendMessage={sendMessage}
      addContactButtonDisabled={addContactButtonDisabled}
      flag={flag}
    />
  );
};

export { EmployeesCardVerticalContainer, EmployeeCardFencing };
